import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Screen } from '@/components/Screen'
import { Kicker } from '@/components/Kicker'
import { Button } from '@/components/Button'
import { Avatar } from '@/components/Avatar'
import { ListRow } from '@/components/ListRow'
import { BottomSheet } from '@/components/BottomSheet'
import { Field, Select, TextInput } from '@/components/Field'
import { supabase } from '@/lib/supabase'
import { useAppStore } from '@/stores/appStore'
import { PACK_COLORS } from '@/design-system/tokens'
import { PlusIcon } from '@/design-system/icons'
import type { FamilyMember, Person, PersonType, RoleInFamily } from '@/lib/types'
import { relativeTime } from '@/lib/format'

export function FamilyScreen() {
  const nav = useNavigate()
  const familyId = useAppStore((s) => s.familyId)
  const persons = useAppStore((s) => s.persons)
  const refresh = useAppStore((s) => s.refreshFamilyContext)
  const focused = useAppStore((s) => s.focusedPersonId)
  const setFocused = useAppStore((s) => s.setFocusedPerson)
  const qc = useQueryClient()

  const [addPersonOpen, setAddPersonOpen] = useState(false)
  const [inviteOpen, setInviteOpen] = useState(false)

  const membersQ = useQuery({
    enabled: !!familyId,
    queryKey: ['family_members', familyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('family_members')
        .select('*')
        .eq('family_id', familyId!)
        .order('joined_at', { ascending: true })
      if (error) throw error
      return (data ?? []) as FamilyMember[]
    },
  })

  const invitesQ = useQuery({
    enabled: !!familyId,
    queryKey: ['pending_invites', familyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('invites')
        .select('jti, invited_email, role, invited_by_user_id, created_at, expires_at, accepted_at, revoked_at')
        .eq('family_id', familyId!)
        .is('accepted_at', null)
        .is('revoked_at', null)
        .gt('expires_at', new Date().toISOString())
        .order('created_at', { ascending: false })
      if (error) throw error
      return (data ?? []) as PendingInvite[]
    },
  })

  return (
    <Screen title="Family">
      <div className="px-4 py-3 space-y-6">
        <section>
          <div className="flex items-center justify-between">
            <Kicker>Persons on the graph</Kicker>
            <button
              type="button"
              onClick={() => setAddPersonOpen(true)}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full text-text-secondary hover:bg-white/5"
              aria-label="Add person"
            >
              <PlusIcon size={18} />
            </button>
          </div>
          <div className="mt-2 rounded-r-sm bg-bg-elevated border border-divider-subtle divide-y divide-divider-subtle">
            {persons.map((p) => (
              <PersonRow
                key={p.id}
                person={p}
                focused={p.id === focused}
                onFocus={() => setFocused(p.id)}
                onOpen={() => nav(`/person/${p.id}`)}
              />
            ))}
            {persons.length === 0 && <div className="p-4 text-body text-text-tertiary">No persons yet — add one to start.</div>}
          </div>
        </section>

        <section>
          <div className="flex items-center justify-between">
            <Kicker>Caregivers</Kicker>
            <button
              type="button"
              onClick={() => setInviteOpen(true)}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full text-text-secondary hover:bg-white/5"
              aria-label="Invite"
            >
              <PlusIcon size={18} />
            </button>
          </div>
          <div className="mt-2 rounded-r-sm bg-bg-elevated border border-divider-subtle divide-y divide-divider-subtle">
            {membersQ.data?.map((m) => (
              <CaregiverRow key={m.id} member={m} />
            ))}
            {membersQ.data?.length === 0 && <div className="p-4 text-body text-text-tertiary">Only you so far.</div>}
          </div>
        </section>

        {invitesQ.data && invitesQ.data.length > 0 && (
          <section>
            <Kicker>Pending invites</Kicker>
            <div className="mt-2 rounded-r-sm bg-bg-elevated border border-divider-subtle divide-y divide-divider-subtle">
              {invitesQ.data.map((inv) => (
                <PendingInviteRow
                  key={inv.jti}
                  invite={inv}
                  onRevoked={() => qc.invalidateQueries({ queryKey: ['pending_invites'] })}
                />
              ))}
            </div>
          </section>
        )}
      </div>

      <AddPersonSheet
        open={addPersonOpen}
        onClose={() => setAddPersonOpen(false)}
        onCreated={async () => {
          await refresh()
        }}
      />
      <InviteSheet
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onCreated={() => qc.invalidateQueries({ queryKey: ['pending_invites'] })}
      />
    </Screen>
  )
}

function PersonRow({ person, focused, onFocus, onOpen }: { person: Person; focused: boolean; onFocus: () => void; onOpen: () => void }) {
  return (
    <div className="flex items-center gap-3 p-3">
      <button type="button" onClick={onFocus} className="flex items-center gap-3 flex-1 min-w-0 text-left">
        <Avatar name={person.display_name} size={40} />
        <div className="min-w-0 flex-1">
          <div className="text-body text-text-primary font-medium truncate">{person.display_name}</div>
          <div className="text-caption text-text-tertiary truncate">
            {person.person_type}
            {focused && <span className="ml-2 text-pack-early-care">· focused</span>}
          </div>
        </div>
      </button>
      <button
        type="button"
        onClick={onOpen}
        className="text-caption text-text-secondary underline underline-offset-2 hover:text-text-primary"
      >
        Open
      </button>
    </div>
  )
}

function CaregiverRow({ member }: { member: FamilyMember }) {
  return (
    <ListRow
      title={`${member.role} · ${member.status}`}
      subtitle={`Joined ${relativeTime(member.joined_at)}`}
    />
  )
}

interface PendingInvite {
  jti: string
  invited_email: string
  role: RoleInFamily
  invited_by_user_id: string | null
  created_at: string
  expires_at: string
  accepted_at: string | null
  revoked_at: string | null
}

function PendingInviteRow({ invite, onRevoked }: { invite: PendingInvite; onRevoked: () => void }) {
  const [busy, setBusy] = useState(false)

  async function revoke() {
    if (!confirm(`Revoke the invite to ${invite.invited_email}?`)) return
    setBusy(true)
    try {
      const { error } = await supabase.functions.invoke('invites/revoke', {
        body: { jti: invite.jti },
      })
      if (error) throw new Error(error.message)
      onRevoked()
    } catch (e) {
      alert(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <ListRow
      title={invite.invited_email}
      subtitle={`${invite.role} · invited ${relativeTime(invite.created_at)} · expires ${relativeTime(invite.expires_at)}`}
      trailing={
        <button
          type="button"
          onClick={revoke}
          disabled={busy}
          className="text-caption text-pack-safety underline underline-offset-2 disabled:opacity-50"
        >
          {busy ? '…' : 'Revoke'}
        </button>
      }
    />
  )
}

function AddPersonSheet({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: () => void | Promise<void> }) {
  const familyId = useAppStore((s) => s.familyId)
  const user = useAppStore((s) => s.user)
  const [displayName, setDisplayName] = useState('')
  const [personType, setPersonType] = useState<PersonType>('baby')
  const [dob, setDob] = useState('')
  const [due, setDue] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  async function create() {
    if (!familyId || !user) return
    setBusy(true); setErr(null)
    try {
      if (!displayName.trim()) throw new Error('Name is required')
      const { error } = await supabase.from('persons').insert({
        family_id: familyId,
        display_name: displayName.trim(),
        person_type: personType,
        date_of_birth: personType === 'baby' || personType === 'child' ? dob || null : null,
        due_date: personType === 'unborn' ? due || null : null,
        identity_status: personType === 'adult' ? 'self_controlled' : 'parent_controlled',
      })
      if (error) throw new Error(error.message)
      await onCreated()
      setDisplayName(''); setDob(''); setDue('')
      onClose()
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title="Add person"
      action={
        <Button variant="primary" size="lg" packColor={PACK_COLORS.early_care.light} loading={busy} onClick={create} className="w-full">
          Add
        </Button>
      }
    >
      <div className="space-y-4 pt-4">
        <Field label="Display name">
          <TextInput value={displayName} onChange={(e) => setDisplayName(e.target.value)} autoFocus />
        </Field>
        <Field label="Person type">
          <Select value={personType} onChange={(e) => setPersonType(e.target.value as PersonType)}>
            <option value="unborn">Unborn</option>
            <option value="baby">Baby</option>
            <option value="child">Child</option>
            <option value="adult">Adult</option>
          </Select>
        </Field>
        {(personType === 'baby' || personType === 'child') && (
          <Field label="Date of birth">
            <TextInput type="date" value={dob} onChange={(e) => setDob(e.target.value)} />
          </Field>
        )}
        {personType === 'unborn' && (
          <Field label="Due date">
            <TextInput type="date" value={due} onChange={(e) => setDue(e.target.value)} />
          </Field>
        )}
        {err && <div className="rounded-r-sm bg-pack-safety/15 border border-pack-safety/30 p-3 text-caption text-pack-safety">{err}</div>}
      </div>
    </BottomSheet>
  )
}

function InviteSheet({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: () => void }) {
  const familyId = useAppStore((s) => s.familyId)
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<RoleInFamily>('caregiver')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [link, setLink] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  async function create() {
    setBusy(true); setErr(null); setLink(null); setCopied(false)
    try {
      if (!familyId) throw new Error('No active family')
      const trimmed = email.trim().toLowerCase()
      if (!trimmed) throw new Error('Email is required — the invite link binds to it, so only the right person can accept.')
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) throw new Error('Enter a valid email address')

      const { data, error } = await supabase.functions.invoke('invites/create', {
        body: { family_id: familyId, invited_email: trimmed, role, expires_hours: 168 },
      })
      if (error) {
        let detail = error.message
        try {
          const anyErr = error as { context?: { text?: () => Promise<string> } }
          if (anyErr.context?.text) {
            const body = await anyErr.context.text()
            const parsed = JSON.parse(body)
            detail = parsed.error ?? parsed.detail ?? body
          }
        } catch { /* noop */ }
        throw new Error(detail)
      }
      if (!data || typeof data !== 'object' || !('token' in data)) {
        throw new Error(`Invite creation returned unexpected shape: ${JSON.stringify(data)}`)
      }
      const token = (data as { token: string }).token
      const url = `${window.location.origin}/join?token=${encodeURIComponent(token)}`
      setLink(url)
      onCreated()
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  async function copyLink() {
    if (!link) return
    try {
      await navigator.clipboard.writeText(link)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      // clipboard API can fail on non-secure contexts — fall back to prompt
      window.prompt('Copy this link:', link)
    }
  }

  function reset() {
    setLink(null); setCopied(false); setEmail(''); setErr(null)
  }

  return (
    <BottomSheet
      open={open}
      onClose={() => { reset(); onClose() }}
      title="Invite caregiver"
      action={
        !link ? (
          <Button variant="primary" size="lg" packColor={PACK_COLORS.early_care.light} loading={busy} onClick={create} className="w-full">
            Create invite link
          </Button>
        ) : (
          <Button variant="primary" size="lg" packColor={PACK_COLORS.early_care.light} onClick={() => { reset(); onClose() }} className="w-full">
            Done
          </Button>
        )
      }
    >
      <div className="space-y-4 pt-4">
        {!link && (
          <>
            <Field label="Their email" hint="The invite binds to this email — only whoever signs in with it can accept.">
              <TextInput
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="partner@example.com"
                required
                autoFocus
              />
            </Field>
            <Field label="Role">
              <Select value={role} onChange={(e) => setRole(e.target.value as RoleInFamily)}>
                <option value="parent">Parent</option>
                <option value="caregiver">Caregiver</option>
                <option value="temporary_caregiver">Temporary caregiver</option>
                <option value="viewer">Viewer (read-only)</option>
              </Select>
            </Field>
            <div className="text-caption text-text-tertiary">
              Invite expires in 7 days. You'll copy the link + send it yourself — Wren never emails caregivers on your behalf.
            </div>
            {err && <div className="rounded-r-sm bg-pack-safety/15 border border-pack-safety/30 p-3 text-caption text-pack-safety whitespace-pre-wrap">{err}</div>}
          </>
        )}
        {link && (
          <>
            <div className="text-body text-text-primary">Share this link with {email}:</div>
            <div className="rounded-r-sm border border-divider-subtle bg-bg-elevated p-3 text-caption font-mono break-all">{link}</div>
            <Button variant="secondary" size="md" onClick={copyLink} className="w-full">
              {copied ? 'Copied ✓' : 'Copy link'}
            </Button>
            <div className="text-caption text-text-tertiary">
              They'll be asked to sign in with {email} to accept. Link expires in 7 days.
            </div>
          </>
        )}
      </div>
    </BottomSheet>
  )
}
